import { Controller } from "@hotwired/stimulus"

// The figure form's YouTube reviews: "Add a YouTube review" copies in a
// blank review's fields (the template, its NEW_RECORD made unique so Rails
// reads each as a new record); a new one can be taken out again.
//
// Pasting (or typing) a video's link looks it up (FiguresController
// #youtube_info): the link is put the standard way, the title and channel
// filled in -- unless typed in by hand -- and a card under it shows the
// video, its numbers, and a word if the figure has it already or it went up
// before the figure was revealed.
export default class extends Controller {
  static targets = ["template", "list"]
  static values = { infoUrl: String, figureId: String }

  add() {
    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, String(Date.now()))
    this.listTarget.insertAdjacentHTML("beforeend", html)
    this.listTarget.lastElementChild?.querySelector("input")?.focus()
  }

  discard(e) {
    e.currentTarget.closest(".youtube-video-review-row")?.remove()
  }

  // Any row's URL field changed: look it up once typing settles.
  lookup(e) {
    const input = e.target
    if (!input.name?.endsWith("[url]")) return
    clearTimeout(input._lookup)
    input._lookup = setTimeout(() => this.fill(input), e.type === "paste" ? 50 : 500)
  }

  async fill(input) {
    const row = input.closest(".youtube-video-review-row")
    const url = input.value.trim()
    if (!row || !url || url === input.dataset.lookedUp) return
    input.dataset.lookedUp = url
    const card = this.card(row)
    card.className = "yt-card is-loading"
    card.textContent = "Looking it up on YouTube…"

    let info
    try {
      const response = await fetch(`${this.infoUrlValue}?${new URLSearchParams({ url, figure_id: this.figureIdValue })}`, { headers: { Accept: "application/json" } })
      info = await response.json()
    } catch {
      info = { error: "Couldn't reach YouTube just now." }
    }
    if (input.value.trim() !== url) return // changed while we looked
    if (info.error) {
      card.className = "yt-card is-error"
      card.textContent = info.error
      return
    }

    input.value = info.url
    input.dataset.lookedUp = info.url
    this.set(row, "[title]", info.title)
    this.set(row, "[channel_name]", info.channel_name)
    this.show(card, info)
  }

  // Fills a field unless someone's typed their own into it.
  set(row, name, value) {
    const field = row.querySelector(`[name$="${name}"]`)
    if (!field || !value) return
    if (field.value && field.value !== field.dataset.filled) return
    field.value = value
    field.dataset.filled = value
  }

  // Under the link, title and channel (a full-width row, before the transcript).
  card(row) {
    let card = row.querySelector(".yt-card")
    if (!card) {
      card = document.createElement("div")
      const transcript = row.querySelector('[name$="[transcript]"]')?.closest("div")
      transcript ? transcript.before(card) : row.append(card)
    }
    return card
  }

  show(card, info) {
    card.className = "yt-card"
    card.replaceChildren()
    const img = document.createElement("img")
    img.src = info.thumbnail
    img.alt = ""
    const body = document.createElement("div")
    const title = document.createElement("b")
    title.textContent = info.title
    const meta = document.createElement("span")
    meta.textContent = [ info.channel_name, info.subscribers_text, info.published && `put up ${info.published}`, info.views_text ].filter(Boolean).join(" · ")
    body.append(title, meta)
    for (const warning of [ info.already && "This figure has this review already.", info.early ].filter(Boolean)) {
      const note = document.createElement("span")
      note.className = "yt-warn"
      note.textContent = warning
      body.append(note)
    }
    card.append(img, body)
  }
}
